import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import SEOMeta from "@/components/SEOMeta";
import ExitIntent from "@/components/ExitIntent";
import { ChevronDown, ChevronRight, HelpCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import PublicNav from "@/components/PublicNav";

const CATEGORY_LABELS = {
  "getting-started": "Getting Started",
  "trading": "Trading",
  "options": "Options & Derivatives",
  "account": "Account & Settings",
  "technical": "Technical & Security",
};

const CATEGORY_ICONS = {
  "getting-started": "🚀",
  "trading": "📈",
  "options": "⚡",
  "account": "👤",
  "technical": "🔒",
};

function MarkdownText({ text }) {
  // Simple markdown: **bold**, inline code, newlines
  const html = text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.*?)`/g, "<code class='bg-[var(--bg-surface-2)] px-1 rounded text-xs font-mono-data'>$1</code>")
    .replace(/\n/g, "<br />");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--border)]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-[var(--bg-surface-2)] transition-colors"
      >
        <span className="font-section text-sm pr-4">{item.question}</span>
        {open ? <ChevronDown className="w-4 h-4 txt-muted flex-shrink-0" /> : <ChevronRight className="w-4 h-4 txt-muted flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 txt-secondary text-sm leading-relaxed">
          <MarkdownText text={item.answer} />
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { user } = useAuth();
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    api.get("/faq").then(({ data }) => {
      setCategories(data.categories || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const allItems = Object.values(categories).flat();
  const filtered = allItems.filter((item) => {
    const matchesCat = activeCategory === "all" || item.category === activeCategory;
    const matchesSearch = !search || item.question.toLowerCase().includes(search.toLowerCase()) || item.answer.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const grouped = {};
  filtered.forEach((item) => {
    grouped[item.category] = grouped[item.category] || [];
    grouped[item.category].push(item);
  });

  const Wrapper = user
    ? AppShell
    : ({ children }) => (
        <div className="min-h-screen bg-[var(--bg-page)] text-white">
          <PublicNav />
          {children}
        </div>
      );

  return (
    <Wrapper>
      <SEOMeta
        title="FAQ — AlgoForge Help & Knowledge Base"
        description="Answers to common questions about algo trading, options strategies, paper trading, broker connections, and AlgoForge platform features."
        url="/faq"
        keywords="algoforge faq, algo trading questions india, options trading help india, paper trading faq"
      />
      <ExitIntent />
      {user && <PageHeader
        overline="Help"
        title="FAQ & Knowledge Base"
        description="Everything you need to know about AlgoForge — trading, options, account management, and more."
      />}
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl">

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 txt-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions…"
            className="pl-9 rounded-none bg-[var(--bg-surface)] border-[var(--border)]"
          />
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2">
          {["all", ...Object.keys(categories)].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs font-section tracking-wider px-3 py-1.5 border transition-colors ${
                activeCategory === cat
                  ? "border-white text-white bg-white/10"
                  : "border-[var(--border)] txt-muted hover:border-[var(--border-strong)] hover:text-white"
              }`}
            >
              {cat === "all" ? "All Topics" : `${CATEGORY_ICONS[cat] || ""} ${CATEGORY_LABELS[cat] || cat}`}
            </button>
          ))}
        </div>

        {loading && <div className="txt-muted text-sm text-center py-8">Loading…</div>}

        {!loading && filtered.length === 0 && (
          <div className="panel p-8 text-center">
            <HelpCircle className="w-8 h-8 txt-muted mx-auto mb-3" />
            <div className="txt-muted text-sm">No results found for "{search}"</div>
          </div>
        )}

        {/* FAQ groups */}
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="panel overflow-hidden">
            <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
              <span className="text-lg">{CATEGORY_ICONS[cat] || "📋"}</span>
              <span className="overline">{CATEGORY_LABELS[cat] || cat}</span>
              <span className="txt-muted text-xs ml-auto">{items.length} articles</span>
            </div>
            {items.map((item) => <FAQItem key={item.id} item={item} />)}
          </div>
        ))}

        {/* Contact / escalate */}
        <div className="panel p-5 border-blue-500/20">
          <div className="overline mb-2">Still need help?</div>
          <div className="txt-secondary text-sm">
            Can't find what you're looking for? Email{" "}
            <a href="mailto:support@algoforge.tech" className="text-blue-400 hover:underline">
              support@algoforge.tech
            </a>{" "}
            or reach out via Telegram.
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
